import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Search, Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_WHITE = '/media/logo/bianco.svg';

// Cross-brand links (Daily-style top strip)
const brandStrip = [
  { label: 'LifeGate Impact', href: 'https://impact.lifegate.it' },
  { label: 'LifeGate Way', href: 'https://lifegateway.it' },
  { label: 'LifeGate Energy', href: 'https://energy.lifegate.it' },
  { label: 'LifeGate Daily', href: 'https://www.lifegate.it' },
  { label: 'LifeGate Edu', href: 'https://lifegateedu.it' },
];

const SUBMENU = { it: 'sottomenu', en: 'submenu', fr: 'sous-menu', de: 'Untermenü', es: 'submenú' };

const Header = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Submit the header search: navigates to the in-site /search page with the
  // query string. Fuse.js runs client-side over static content + CMS overrides
  // (see /app/frontend/src/lib/searchIndex.js). Replaces the old Google
  // site-search redirect per user request.
  const submitSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchQuery('');
    setSearchOpen(false);
  };

  const NavItem = ({ label, items, href }) => {
    // If href is provided the label is a clickable Link to the overview page;
    // the chevron button opens the sub-menu on hover/click ("split trigger" pattern).
    // Without href we fall back to the original hover-to-open behaviour.
    if (href) {
      return (
        <DropdownMenu>
          <div className="flex items-center gap-1 group">
            <Link
              to={href}
              className="nav-link text-[12.5px] font-medium uppercase tracking-[0.15em] text-white/90 hover:text-[#22C55E]"
              data-testid={`nav-${href.replace(/^\//, '')}`}
            >
              {label}
            </Link>
            <DropdownMenuTrigger
              className="p-1 text-white/70 hover:text-[#22C55E] outline-none"
              aria-label={`${label} – ${SUBMENU[lang]}`}
              data-testid={`nav-${href.replace(/^\//, '')}-submenu`}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
          </div>
          <DropdownMenuContent align="start" className="min-w-[240px] mt-2 bg-[#1B2F1B] border border-white/10 text-white/90 shadow-xl rounded-none">
            {items.map((item) => (
              <DropdownMenuItem key={item.label} asChild>
                <Link to={item.href} className="cursor-pointer text-white/90 hover:!text-[#22C55E] hover:!bg-white/[0.04] focus:!bg-white/[0.04] focus:!text-[#22C55E] text-[13px] tracking-wide">
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger className="nav-link flex items-center gap-1.5 text-[12.5px] font-medium uppercase tracking-[0.15em] text-white/90 hover:text-[#22C55E] outline-none">
          {label}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[240px] mt-2 bg-[#1B2F1B] border border-white/10 text-white/90 shadow-xl rounded-none">
          {items.map((item) => (
            <DropdownMenuItem key={item.label} asChild>
              <Link to={item.href} className="cursor-pointer text-white/90 hover:!text-[#22C55E] hover:!bg-white/[0.04] focus:!bg-white/[0.04] focus:!text-[#22C55E] text-[13px] tracking-wide">
                {item.label}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      {/* Cross-brand top strip */}
      <div className="lg-topbar hidden md:block" data-testid="topbar-strip">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center h-9">
          <div className="flex items-center gap-0">
            {brandStrip.map((b, i) => (
              <React.Fragment key={b.label}>
                {i > 0 && <span className="dot" aria-hidden />}
                <a href={b.href} target="_blank" rel="noreferrer" className="hover:text-[#22C55E] transition-colors" data-testid={`topbar-link-${i}`}>
                  {b.label}
                </a>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main header — unified deep green */}
      <header
        className="sticky top-0 z-50 bg-[#1B2F1B] border-b border-white/[0.06]"
        data-testid="main-header"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-[76px]">
            <Link to="/" className="flex items-center group" data-testid="header-logo">
              <img
                src={LOGO_WHITE}
                alt="LifeGate Company"
                className="h-9 md:h-10 w-auto"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-9">
              <NavItem label={t.nav.aboutUs} items={t.aboutSubmenu} href="/about-us" />
              <Link to="/stakeholders" className="nav-link text-[12.5px] font-medium uppercase tracking-[0.15em] text-white/90 hover:text-[#22C55E]">
                {t.nav.stakeholders}
              </Link>
              <NavItem label={t.nav.whatWeDo} items={t.whatWeDoSubmenu} href="/what-we-do" />
              <Link to="/careers" className="nav-link text-[12.5px] font-medium uppercase tracking-[0.15em] text-white/90 hover:text-[#22C55E]" data-testid="nav-careers">
                {t.nav.careers}
              </Link>
              <Link to="/contacts" className="nav-link text-[12.5px] font-medium uppercase tracking-[0.15em] text-white/90 hover:text-[#22C55E]">
                {t.nav.contacts}
              </Link>

              <div className="flex items-center gap-2 pl-6 border-l border-white/10">
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-1.5 text-[11.5px] font-semibold text-white/85 hover:text-[#22C55E] outline-none tracking-wider" data-testid="lang-trigger">
                    <Globe className="h-3.5 w-3.5" />
                    {lang.toUpperCase()}
                    <ChevronDown className="h-3 w-3 opacity-60" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[140px] mt-2 bg-[#1B2F1B] border border-white/10 rounded-none">
                    {languages.map((l) => (
                      <DropdownMenuItem
                        key={l.code}
                        onClick={() => setLang(l.code)}
                        className={`cursor-pointer ${lang === l.code ? 'text-[#22C55E] font-semibold' : 'text-white/90'} hover:!bg-white/[0.04] hover:!text-[#22C55E] focus:!bg-white/[0.04]`}
                        data-testid={`lang-option-${l.code}`}
                      >
                        {l.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <button
                  onClick={() => setSearchOpen(!searchOpen)}
                  aria-label={{ it: 'Cerca nel sito', en: 'Search the site', fr: 'Rechercher sur le site', de: 'Website durchsuchen', es: 'Buscar en el sitio' }[lang]}
                  className="text-white/70 hover:text-[#22C55E] transition p-1"
                  data-testid="header-search-toggle"
                >
                  <Search className="h-[17px] w-[17px]" />
                </button>
              </div>
            </nav>

            <div className="lg:hidden flex items-center gap-3">
              {/* Compact language switcher — always visible on mobile so users can change
                  language without opening the full mobile menu (matches Iubenda banner auto-sync). */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  className="flex items-center gap-1 text-[12px] font-semibold text-white/90 hover:text-[#22C55E] outline-none tracking-wider px-1.5 py-1"
                  data-testid="lang-trigger-mobile"
                  aria-label={{ it: 'Cambia lingua', en: 'Change language', fr: 'Changer de langue', de: 'Sprache ändern', es: 'Cambiar idioma' }[lang]}
                >
                  <Globe className="h-4 w-4" />
                  <span>{lang.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[140px] mt-2 bg-[#1B2F1B] border border-white/10 rounded-none">
                  {languages.map((l) => (
                    <DropdownMenuItem
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      className={`cursor-pointer ${lang === l.code ? 'text-[#22C55E] font-semibold' : 'text-white/90'} hover:!bg-white/[0.04] hover:!text-[#22C55E] focus:!bg-white/[0.04]`}
                      data-testid={`lang-option-mobile-${l.code}`}
                    >
                      {l.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="text-white"
                aria-label={{ it: 'Apri il menu', en: 'Open menu', fr: 'Ouvrir le menu', de: 'Menü öffnen', es: 'Abrir el menú' }[lang]}
                data-testid="header-mobile-toggle"
              >
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {searchOpen && (
            <div className="pb-4 animate-fade-in">
              <form
                onSubmit={(e) => { e.preventDefault(); submitSearch(); }}
                data-testid="header-search-form"
              >
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); }
                  }}
                  placeholder={t.nav.search + '...'}
                  autoFocus
                  className="w-full border-b-2 border-[#22C55E] py-2 text-lg outline-none bg-transparent text-white placeholder-white/40"
                  data-testid="header-search-input"
                  aria-label={t.nav.search}
                />
              </form>
            </div>
          )}
        </div>

        {mobileOpen && (
          <div className="lg:hidden bg-[#1B2F1B] border-t border-white/[0.06] max-h-[calc(100vh-5rem)] overflow-y-auto text-white">
            <div className="px-6 py-6 space-y-5">
              <MobileSection title={t.nav.aboutUs} items={t.aboutSubmenu} href="/about-us" onNav={() => setMobileOpen(false)} />
              <Link to="/stakeholders" className="block text-white/95 font-medium uppercase tracking-[0.15em] text-sm">{t.nav.stakeholders}</Link>
              <MobileSection title={t.nav.whatWeDo} items={t.whatWeDoSubmenu} href="/what-we-do" onNav={() => setMobileOpen(false)} />
              <Link to="/careers" className="block text-white/95 font-medium uppercase tracking-[0.15em] text-sm" onClick={() => setMobileOpen(false)}>{t.nav.careers}</Link>
              <Link to="/contacts" className="block text-white/95 font-medium uppercase tracking-[0.15em] text-sm">{t.nav.contacts}</Link>
              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center gap-2 text-xs text-white/60 mb-3 uppercase tracking-wider">
                  <Globe className="h-3.5 w-3.5" /> Language
                </div>
                <div className="flex flex-wrap gap-2">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition ${
                        lang === l.code ? 'bg-[#22C55E] text-[#1B2F1B] border-[#22C55E]' : 'border-white/25 text-white/85'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="pt-4 border-t border-white/10 space-y-2">
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#22C55E] font-semibold mb-2">{{ fr: 'Réseau', de: 'Netzwerk', es: 'Red' }[lang] || 'Network'}</p>
                {brandStrip.map((b) => (
                  <a key={b.label} href={b.href} target="_blank" rel="noreferrer" className="block text-white/70 text-sm hover:text-[#22C55E]">
                    {b.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

const MobileSection = ({ title, items, href, onNav }) => {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <div className="w-full flex justify-between items-center">
        {href ? (
          <Link
            to={href}
            onClick={onNav}
            className="text-white/95 font-medium uppercase tracking-[0.15em] text-sm hover:text-[#22C55E] flex-1 text-left"
          >
            {title}
          </Link>
        ) : (
          <span className="text-white/95 font-medium uppercase tracking-[0.15em] text-sm flex-1 text-left">{title}</span>
        )}
        <button
          onClick={() => setOpen(!open)}
          aria-label={`${title} – ${SUBMENU[lang]}`}
          className="p-2 -mr-2 text-white/70 hover:text-[#22C55E]"
        >
          <ChevronDown className={`h-4 w-4 transition ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && (
        <div className="mt-3 pl-4 space-y-2">
          {items.map((it) => (
            <Link key={it.label} to={it.href} onClick={onNav} className="block text-white/70 text-sm hover:text-[#22C55E]">
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Header;
